import {
  clearOfflinePackMetas,
  deleteOfflinePackMeta,
  deleteOfflinePacksForRoute,
  listOfflinePacks,
  notifyOfflinePacksChanged,
  type OfflinePackMeta,
  type OfflinePackStyle,
} from './offline-pack-db.js'

export type { OfflinePackMeta, OfflinePackStyle } from './offline-pack-db.js'

export type BBox = [number, number, number, number]

export type TileSpec = {
  z: number
  x: number
  y: number
  style?: OfflinePackStyle
}

export type PrefetchProgress = {
  done: number
  total: number
  bytes: number
  skipped: number
}

export type PrefetchTilesOptions = {
  concurrency?: number
  signal?: AbortSignal
}

type TileUrlTemplate = string | ((spec: TileSpec) => string)

export const OFFLINE_TILE_CACHE = 'xr-offline-tiles-v1'
export const MAPBOX_RUNTIME_TILE_CACHE = 'xr-mapbox-runtime-tiles'

const MAX_MERCATOR_LAT = 85.05112878
const ESTIMATED_BYTES_PER_TILE = 35 * 1024

const MAPBOX_RASTER_TILESETS: Record<OfflinePackStyle, string> = {
  standard: 'mapbox.mapbox-streets-v8',
  outdoor: 'mapbox.outdoors',
  satellite: 'mapbox.satellite',
}

class TileHttpError extends Error {
  constructor(public readonly status: number) {
    super(`Tile fetch failed: ${status}`)
    this.name = 'TileHttpError'
  }
}

class TileNetworkError extends Error {
  constructor(cause: unknown) {
    super(cause instanceof Error ? cause.message : 'Tile network request failed')
    this.name = 'TileNetworkError'
  }
}

export function enumerateTiles(bbox: BBox, zoomRange: { min: number; max: number }): TileSpec[] {
  const minZoom = Math.max(0, Math.floor(Math.min(zoomRange.min, zoomRange.max)))
  const maxZoom = Math.max(minZoom, Math.floor(Math.max(zoomRange.min, zoomRange.max)))
  const [minLonRaw, minLatRaw, maxLonRaw, maxLatRaw] = normalizeBbox(bbox)
  const minLat = clamp(minLatRaw, -MAX_MERCATOR_LAT, MAX_MERCATOR_LAT)
  const maxLat = clamp(maxLatRaw, -MAX_MERCATOR_LAT, MAX_MERCATOR_LAT)
  const minLon = clamp(minLonRaw, -180, 180)
  const maxLon = clamp(maxLonRaw, -180, 180)
  const specs: TileSpec[] = []

  for (let z = minZoom; z <= maxZoom; z++) {
    const nw = lonLatToTile(minLon, maxLat, z)
    const se = lonLatToTile(maxLon, minLat, z)
    const n = 2 ** z
    const minX = clamp(Math.min(nw.x, se.x), 0, n - 1)
    const maxX = clamp(Math.max(nw.x, se.x), 0, n - 1)
    const minY = clamp(Math.min(nw.y, se.y), 0, n - 1)
    const maxY = clamp(Math.max(nw.y, se.y), 0, n - 1)
    for (let x = minX; x <= maxX; x++) {
      for (let y = minY; y <= maxY; y++) {
        specs.push({ z, x, y })
      }
    }
  }
  return specs
}

export function estimateBytes(tileSpecs: TileSpec[]): { count: number; bytes: number } {
  return { count: tileSpecs.length, bytes: tileSpecs.length * ESTIMATED_BYTES_PER_TILE }
}

export async function* prefetchTiles(
  specs: TileSpec[],
  urlTemplate: TileUrlTemplate,
  opts: PrefetchTilesOptions = {},
): AsyncIterable<PrefetchProgress> {
  if (typeof caches === 'undefined') throw new Error('Cache Storage is not available')

  const total = specs.length
  const concurrency = Math.max(1, Math.min(8, opts.concurrency ?? 4))
  const cache = await caches.open(OFFLINE_TILE_CACHE)
  let next = 0
  let done = 0
  let bytes = 0
  let skipped = 0
  const inFlight = new Map<number, Promise<{ index: number; bytes: number; skipped: boolean }>>()

  const launch = () => {
    while (inFlight.size < concurrency && next < total) {
      if (opts.signal?.aborted) throw abortError()
      const index = next
      next += 1
      const spec = specs[index]!
      inFlight.set(
        index,
        fetchTileWithRetry(cache, resolveTileUrl(urlTemplate, spec), opts.signal).then(
          (result) => ({
            index,
            bytes: result.bytes,
            skipped: result.skipped,
          }),
        ),
      )
    }
  }

  try {
    launch()
    while (inFlight.size > 0) {
      if (opts.signal?.aborted) throw abortError()
      const result = await Promise.race(inFlight.values())
      inFlight.delete(result.index)
      done += 1
      bytes += result.bytes
      if (result.skipped) skipped += 1
      yield { done, total, bytes, skipped }
      launch()
    }
    if (total > 0 && skipped === total) {
      throw new Error('Offline tile prefetch failed: no tiles were cached')
    }
  } catch (err) {
    await Promise.allSettled(inFlight.values())
    throw err
  }
}

export async function clearOfflinePack(routeId: string, style?: OfflinePackStyle): Promise<void> {
  if (typeof caches !== 'undefined') {
    const cache = await caches.open(OFFLINE_TILE_CACHE)
    const keys = await cache.keys()
    await Promise.all(
      keys.map(async (request) => {
        const url = new URL(request.url)
        if (url.searchParams.get('xr-route') !== routeId) return
        if (style && url.searchParams.get('xr-style') !== style) return
        await cache.delete(request)
      }),
    )
  }
  if (style) await deleteOfflinePackMeta(routeId, style)
  else await deleteOfflinePacksForRoute(routeId)
  notifyOfflinePacksChanged()
}

export async function clearRoutePack(routeId: string): Promise<void> {
  await clearOfflinePack(routeId)
}

export async function clearAllOfflinePacks(): Promise<void> {
  if (typeof caches !== 'undefined') await caches.delete(OFFLINE_TILE_CACHE)
  await clearOfflinePackMetas()
  notifyOfflinePacksChanged()
}

export async function listInstalledPacks(): Promise<OfflinePackMeta[]> {
  return listOfflinePacks()
}

export async function getOfflineTileCacheUsage(routeId?: string): Promise<number> {
  if (typeof caches === 'undefined') return 0
  const cache = await caches.open(OFFLINE_TILE_CACHE)
  const keys = await cache.keys()
  let bytes = 0
  for (const request of keys) {
    if (routeId) {
      const url = new URL(request.url)
      if (url.searchParams.get('xr-route') !== routeId) continue
    }
    const response = await cache.match(request)
    bytes += await responseSize(response)
  }
  return bytes
}

export function withStyle(specs: TileSpec[], style: OfflinePackStyle): TileSpec[] {
  return specs.map((spec) => ({ ...spec, style }))
}

export function expandBboxByKm(bbox: BBox, bufferKm: number): BBox {
  const [minLon, minLat, maxLon, maxLat] = normalizeBbox(bbox)
  const midLat = (minLat + maxLat) / 2
  const latDelta = bufferKm / 111.32
  const lonDelta = bufferKm / (111.32 * Math.max(0.2, Math.cos((midLat * Math.PI) / 180)))
  return [
    clamp(minLon - lonDelta, -180, 180),
    clamp(minLat - latDelta, -MAX_MERCATOR_LAT, MAX_MERCATOR_LAT),
    clamp(maxLon + lonDelta, -180, 180),
    clamp(maxLat + latDelta, -MAX_MERCATOR_LAT, MAX_MERCATOR_LAT),
  ]
}

export function mapboxRasterTileUrl(spec: TileSpec, token: string, routeId: string): string {
  const style = spec.style ?? 'standard'
  const url = new URL(
    `https://api.mapbox.com/v4/${MAPBOX_RASTER_TILESETS[style]}/${spec.z}/${spec.x}/${spec.y}@2x.png`,
  )
  url.searchParams.set('access_token', token)
  url.searchParams.set('xr-route', routeId)
  url.searchParams.set('xr-style', style)
  return url.toString()
}

async function fetchTileWithRetry(
  cache: Cache,
  url: string,
  signal: AbortSignal | undefined,
): Promise<{ bytes: number; skipped: boolean }> {
  try {
    return await fetchTile(cache, url, signal)
  } catch (err) {
    if (signal?.aborted) throw err
    if (!isSkippableTileError(err)) throw err
    try {
      return await fetchTile(cache, url, signal)
    } catch (retryErr) {
      if (signal?.aborted) throw retryErr
      if (!isSkippableTileError(retryErr)) throw retryErr
      return { bytes: 0, skipped: true }
    }
  }
}

async function fetchTile(
  cache: Cache,
  url: string,
  signal: AbortSignal | undefined,
): Promise<{ bytes: number; skipped: boolean }> {
  const request = new Request(url, { mode: 'cors' })
  let response: Response
  try {
    response = await fetch(request, { signal })
  } catch (err) {
    if (signal?.aborted) throw err
    throw new TileNetworkError(err)
  }
  if (response.status === 404) return { bytes: 0, skipped: true }
  if (!response.ok) throw new TileHttpError(response.status)
  const cacheResponse = response.clone()
  const byteCount = await responseSize(response)
  await cache.put(request, cacheResponse)
  return { bytes: byteCount, skipped: false }
}

function isSkippableTileError(err: unknown): boolean {
  if (err instanceof TileHttpError) {
    return err.status === 408 || err.status === 429 || err.status >= 500
  }
  return err instanceof TileNetworkError
}

async function responseSize(response: Response | undefined): Promise<number> {
  if (!response) return 0
  const header = Number(response.headers.get('content-length'))
  if (Number.isFinite(header) && header > 0) return header
  return (await response.blob()).size
}

function resolveTileUrl(template: TileUrlTemplate, spec: TileSpec): string {
  if (typeof template === 'function') return template(spec)
  return template
    .replaceAll('{z}', String(spec.z))
    .replaceAll('{x}', String(spec.x))
    .replaceAll('{y}', String(spec.y))
    .replaceAll('{style}', spec.style ?? 'standard')
}

function lonLatToTile(lon: number, lat: number, z: number): { x: number; y: number } {
  const n = 2 ** z
  const latRad = (lat * Math.PI) / 180
  return {
    x: Math.floor(((lon + 180) / 360) * n),
    y: Math.floor(((1 - Math.log(Math.tan(latRad) + 1 / Math.cos(latRad)) / Math.PI) / 2) * n),
  }
}

function normalizeBbox(bbox: BBox): BBox {
  const [aLon, aLat, bLon, bLat] = bbox
  return [Math.min(aLon, bLon), Math.min(aLat, bLat), Math.max(aLon, bLon), Math.max(aLat, bLat)]
}

function clamp(value: number, min: number, max: number): number {
  return Math.max(min, Math.min(max, value))
}

function abortError(): DOMException {
  return new DOMException('Offline tile prefetch cancelled', 'AbortError')
}
